import { ExternalLink } from "lucide-mui";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { formatMoney } from "../../../utils/format.ts";
import { keys } from "../../../api/queryKeys.ts";
import { api } from "../../../api/client.ts";
import type { TenderListItem } from "../../../api/client.ts";
import CpvCodeList from "../../../components/CpvCode.tsx";
import { DetailGridItem } from "../../../components/DetailRow.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import ExpandableRow from "../../../components/table/ExpandableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { tenderNoticeTypeColor } from "../../../utils/statusColors.ts";

interface TenderRowProps {
  tender: TenderListItem;
  companyId: string;
}

function TenderRow({ tender, companyId }: TenderRowProps) {
  const isBuyer = tender.buyer_company_id === companyId;
  const isWinner = tender.winner_company_id === companyId;
  const value = tender.awarded_value ?? tender.estimated_value;
  const valueLabel = tender.awarded_value ? "Awarded" : "Estimated";

  return (
    <ExpandableRow
      colSpan={6}
      cells={
        <>
          <TableCell>
            <Chip
              size="small"
              label={tender.notice_type}
              color={tenderNoticeTypeColor(tender.notice_type)}
              variant="outlined"
            />
          </TableCell>
          <TableCell sx={{ maxWidth: 320 }}>
            {tender.source_url ? (
              <Link
                href={tender.source_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {tender.title} <ExternalLink fontSize="inherit" />
              </Link>
            ) : (
              tender.title
            )}
          </TableCell>
          <TableCell>
            <Stack direction="row" spacing={0.5}>
              {isBuyer && <Chip size="small" label="Buyer" color="info" variant="outlined" />}
              {isWinner && <Chip size="small" label="Winner" color="success" variant="outlined" />}
            </Stack>
          </TableCell>
          <TableCell align="right">
            {value ? (
              <Stack sx={{ alignItems: "flex-end" }}>
                <Typography variant="body2">{formatMoney(value, tender.currency, { decimals: 0 })}</Typography>
                <Typography variant="caption" color="text.secondary">{valueLabel}</Typography>
              </Stack>
            ) : "—"}
          </TableCell>
          <TableCell>
            <FormattedDate value={tender.publication_date} format="date" />
          </TableCell>
        </>
      }
    >
      <Grid container spacing={2}>
        <DetailGridItem label="Buyer" value={tender.buyer_name} />
        <DetailGridItem label="Winner" value={tender.winner_name} />
        <DetailGridItem label="Procedure" value={tender.procedure_type} />
        <DetailGridItem label="Subtype" value={tender.notice_subtype} />
        <DetailGridItem
          label="Deadline"
          value={
            tender.submission_deadline && (
              <FormattedDate value={tender.submission_deadline} format="datetime" />
            )
          }
        />
        <DetailGridItem
          label="Award date"
          value={tender.award_date && <FormattedDate value={tender.award_date} format="date" />}
        />
        <DetailGridItem
          label="Tenders received"
          value={tender.num_tenders_received != null ? String(tender.num_tenders_received) : null}
        />
        <DetailGridItem
          wide
          label="CPV codes"
          value={
            tender.cpv_codes && tender.cpv_codes.length > 0 ? (
              <CpvCodeList codes={tender.cpv_codes} />
            ) : null
          }
        />
        <DetailGridItem label="Framework agreement" value={tender.is_framework_agreement ? "Yes" : null} />
      </Grid>
    </ExpandableRow>
  );
}

interface TendersSectionProps {
  companyId: string;
}

export default function TendersSection({ companyId }: TendersSectionProps) {
  const { data, isError, isLoading } = useQuery({
    queryKey: keys.masterCompany.tenders(companyId),
    queryFn: () => api.getCompanyTenders(companyId, { limit: 100 }),
    retry: false,
  });

  if (isError || isLoading) return null;
  const items = data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <Panel title="Tenders" count={data?.total ?? items.length} sx={{ mb: 3 }}>
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Type</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right">Value</TableCell>
            <TableCell>Published</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((tender) => (
            <TenderRow key={tender.id} tender={tender} companyId={companyId} />
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}
